/**
 * The Google Photos sign-in's end in the app's tab: waiting for
 * google-photos.html (googlePhotosReturn) to hand over the session it made
 * after Google's sign-in, and reporting a sign-in that failed in the window
 * while the tab that started it was gone.
 *
 * Moved word for word out of GooglePhotosService (W1.18 part 3, 2026-10-26),
 * which starts the sign-in from a tap (buildAuthUrl, writeRequest) and calls
 * these; the keys and the handoff's reading are googlePhotosFlow's.
 *
 * Requests: none. Browser storage: the Google Photos sign-in keys (the
 * request, the handoff) and the pending pick in localStorage, read and taken
 * through googlePhotosFlow with the store GooglePhotosService hands in.
 *
 * @module googlePhotosSignIn
 */

import {
  GOOGLE_PHOTOS_HANDOFF_KEY,
  GOOGLE_PHOTOS_PENDING_CLAIM_MS,
  GOOGLE_PHOTOS_SIGNIN_MAX_WAIT_MS,
} from '@/config/tunables/shared';
import {
  GooglePhotosError,
  HANDOFF_MESSAGE,
  clearRequest,
  readHandoff,
  readPending,
  readRequest,
  takeHandoff,
  type Handoff,
} from './googlePhotosFlow';
import { reportFailure } from './googlePhotosSession';

/**
 * Waits for google-photos.html to hand over the session: by localStorage
 * (its storage event, a check each second, and a check when the tab comes
 * back) or by a message to this window, whichever comes first. A pick the
 * window kept (PendingPick) counts too, should the handoff itself be gone.
 *
 * @param store - localStorage.
 * @param state - The sign-in's state.
 * @param signal - Cancels the wait.
 * @returns The handoff, or 'pending' when only the kept pick was found.
 * @throws GooglePhotosError 'cancelled' or 'timed-out'.
 */
export function waitForHandoff(store: Storage, state: string, signal: AbortSignal): Promise<Handoff | 'pending'> {
  return new Promise((resolve, reject) => {
    /** Stops listening. */
    const cleanup = () => {
      clearInterval(ticker);
      clearTimeout(timer);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', check);
      signal.removeEventListener('abort', onAbort);
    };
    /**
     * Looks for the handoff in storage.
     *
     * @returns True when it was found.
     */
    function check(): boolean {
      const handoff = takeHandoff(store, state, Date.now());
      if (handoff) { cleanup(); resolve(handoff); }
      return Boolean(handoff);
    }
    /** Another tab wrote to storage. */
    const onStorage = (event: StorageEvent) => { if (event.key === GOOGLE_PHOTOS_HANDOFF_KEY) check(); };
    /** The window posted the handoff here. */
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; handoff?: Handoff } | null;
      if (event.origin !== window.location.origin || data?.type !== HANDOFF_MESSAGE) return;
      if (data.handoff?.state !== state) return;
      takeHandoff(store, state, Date.now());
      cleanup();
      resolve(data.handoff);
    };
    /** Cancelled. */
    const onAbort = () => { cleanup(); reject(new GooglePhotosError('cancelled', 'The pick was cancelled.')); };
    /**
     * Time's up. A tab a phone froze wakes here late: one more look, and a
     * pick the window kept is still a pick.
     */
    const onTimeout = () => {
      if (check()) return;
      cleanup();
      if (readPending(store)?.pickId === state) resolve('pending');
      else reject(new GooglePhotosError('timed-out', 'Google sign-in was left open without finishing.'));
    };

    const ticker = setInterval(check, 1000);
    const timer = setTimeout(onTimeout, GOOGLE_PHOTOS_SIGNIN_MAX_WAIT_MS);
    window.addEventListener('storage', onStorage);
    window.addEventListener('message', onMessage);
    document.addEventListener('visibilitychange', check);
    signal.addEventListener('abort', onAbort, { once: true });
    if (signal.aborted) onAbort();
    else check();
  });
}

/**
 * A sign-in that failed in the window while the tab that started it was
 * gone (a phone threw it away): nobody else would hear of it, so it is
 * sent to Sentry here and the handoff taken. One still fresh is left for
 * its own tab; any handoff past the sign-in's wait is thrown away, token
 * and all.
 *
 * @param store - localStorage.
 * @param livePickId - The pick a tap in this tab started, if any: its own
 *   handoff is left for it.
 */
export function reportStrandedFailure(store: Storage, livePickId: string | undefined): void {
  const handoff = readHandoff(store);
  if (!handoff) return;
  const now = Date.now();
  const age = now - Number(handoff.at);
  if (age > GOOGLE_PHOTOS_SIGNIN_MAX_WAIT_MS) {
    takeHandoff(store, handoff.state, now);
    return;
  }
  if (handoff.ok || livePickId === handoff.state || age < GOOGLE_PHOTOS_PENDING_CLAIM_MS) return;
  if (!readRequest(store, now)) return;
  takeHandoff(store, handoff.state, now);
  clearRequest(store);
  reportFailure(new GooglePhotosError(handoff.reason, handoff.detail), 'sign-in');
}
